'use client';

import { ClipboardList, User, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WizardProgress, type WizardStep } from '@kit/ui/wizard-progress';

export type ApplyJourneyStep = 'signup' | 'verify' | 'profile' | 'apply';

const STEP_INDEX: Record<ApplyJourneyStep, number> = {
  signup: 0,
  verify: 0,
  profile: 1,
  apply: 2,
};

interface ApplyJourneyProgressProps {
  step: ApplyJourneyStep;
  auditionTitle: string;
}

/**
 * Three-step progress indicator shown across the guided apply journey.
 * Sign up → Profile → Apply. The "verify" sub-state lives inside the
 * sign-up phase, so it shares step 0.
 *
 * Always rendered above the page content with the audition title so the
 * user knows which audition triggered the flow even after refresh or
 * navigation.
 */
export function ApplyJourneyProgress({
  step,
  auditionTitle,
}: ApplyJourneyProgressProps) {
  const { t } = useTranslation('auditions');
  const currentStepIndex = STEP_INDEX[step];

  const steps: WizardStep[] = [
    {
      id: 'signup',
      label: t('applyJourney.steps.signup'),
      icon: UserPlus,
    },
    {
      id: 'profile',
      label: t('applyJourney.steps.profile'),
      icon: User,
    },
    {
      id: 'apply',
      label: t('applyJourney.steps.apply'),
      icon: ClipboardList,
    },
  ];

  return (
    <div className="space-y-4 pb-2">
      <p className="text-muted-foreground text-center text-sm">
        {t('applyJourney.applyingTo', { title: auditionTitle })}
      </p>
      <WizardProgress
        variant="icons"
        steps={steps}
        currentStep={currentStepIndex}
      />
    </div>
  );
}
